import React, { useEffect, useMemo, useRef, useState } from 'react';

import useIntersectionObserver from '@/components/timeline/timelineObserver';

// ─────────────────────────────────────────────────────────────────────────────
// HOW IT WORKS — 3계층 플로팅 다이어그램
// 메타데이터 어휘사전(하) → 데이터셋(중) → 데이터맵(상) 으로
// 아래에서 위로 정제·시각화되는 구조를 3D 카드 스택으로 표현.
// 화이트 코퍼레이트 톤(.cp). 화면에 들어오면 아래 레이어부터 떠오르며
// (조금 확대) 노드/연결선이 그려진다. 레이어 호버 시 강조·확대.
// 색상은 홈페이지 데이터맵 그래프와 동일한 멀티컬러 팔레트를 사용.
// ─────────────────────────────────────────────────────────────────────────────

const STAGE_W = 1200;
const STAGE_H = 890;

// 홈페이지 데이터맵(DataMapMain) 그래프와 동일 팔레트
const PALETTE = ['#2B8CE6', '#1F6FD6', '#4FB0F0', '#F2A33C', '#5FAE3D', '#1F5A78', '#4A43C7'];

type Layer = 'map' | 'set' | 'meta';

// ── 데이터맵: 중심 → L1 → L2 → L3 동심(레벨) 그래프 ──────────────────────────
type MapNode = { x: number; y: number; rad: number; c: string; hub?: boolean; level: number };
type MapEdge = { a: number; b: number; lvl: number };

function buildMap() {
  const W = 520;
  const H = 372;
  const mcx = W / 2;
  const mcy = H / 2;
  const nodes: MapNode[] = [{ x: mcx, y: mcy, rad: 21, c: '#2B8CE6', hub: true, level: 0 }];
  const r1: number[] = [];
  const r2: number[] = [];
  const ring1N = 5;
  const R1 = 72;
  const ring2N = 12;
  const R2x = 150;
  const R2y = 140;
  const R3x = 208;
  const R3y = 160;

  for (let i = 0; i < ring1N; i++) {
    const a = -Math.PI / 2 + i * ((2 * Math.PI) / ring1N);
    nodes.push({
      x: mcx + Math.cos(a) * R1,
      y: mcy + Math.sin(a) * R1,
      rad: 11,
      c: PALETTE[i % PALETTE.length],
      level: 1,
    });
    r1.push(nodes.length - 1);
  }
  for (let i = 0; i < ring2N; i++) {
    const a = -Math.PI / 2 + i * ((2 * Math.PI) / ring2N) + 0.26;
    nodes.push({
      x: mcx + Math.cos(a) * R2x,
      y: mcy + Math.sin(a) * R2y,
      rad: 5.5 + (i % 3 === 0 ? 2.5 : 0),
      c: PALETTE[(i + 2) % PALETTE.length],
      level: 2,
    });
    r2.push(nodes.length - 1);
  }

  const edges: MapEdge[] = [];
  // 중심 → L1 (유사도, 점선 시안)
  r1.forEach((idx) => edges.push({ a: 0, b: idx, lvl: 1 }));
  // L1 → L2 (가까운 2개)
  r1.forEach((idx) => {
    const p = nodes[idx];
    const near = r2
      .map((j) => ({ j, d: (nodes[j].x - p.x) ** 2 + (nodes[j].y - p.y) ** 2 }))
      .sort((a, b) => a.d - b.d)
      .slice(0, 2);
    near.forEach((o) => edges.push({ a: idx, b: o.j, lvl: 2 }));
  });
  // L2 → L3 외곽 리프 노드(마지막 레벨 밀도)
  r2.forEach((idx, i) => {
    if (i % 5 === 4) return;
    const p = nodes[idx];
    const ba = Math.atan2(p.y - mcy, p.x - mcx);
    const spread = i % 3 === 0 ? 2 : 1;
    for (let k = 0; k < spread; k++) {
      const a = ba + (spread === 1 ? 0 : k === 0 ? -0.16 : 0.16);
      nodes.push({
        x: mcx + Math.cos(a) * R3x,
        y: mcy + Math.sin(a) * R3y,
        rad: 4 + (i % 4 === 0 ? 1.5 : 0),
        c: PALETTE[(i + k + 4) % PALETTE.length],
        level: 3,
      });
      edges.push({ a: idx, b: nodes.length - 1, lvl: 3 });
    }
  });

  const guides = [R1, (R2x + R2y) / 2, (R3x + R3y) / 2];
  return { W, H, mcx, mcy, nodes, edges, guides };
}

// ── 데이터셋: ERD 테이블 + 관계선 ────────────────────────────────────────────
type Table = { x: number; y: number; w: number; h: number; hue: string; rows: number };
type Rel = { a: number; b: number; m: number };

function buildSet() {
  const TW = 80;
  const TH = 52;
  const tpos = [
    { x: 40, y: 34 },
    { x: 222, y: 24 },
    { x: 400, y: 46 },
    { x: 128, y: 150 },
    { x: 316, y: 148 },
    { x: 46, y: 268 },
    { x: 236, y: 272 },
    { x: 412, y: 262 },
  ];
  const thues = ['#2B8CE6', '#1F6FD6', '#4FB0F0', '#1F5A78', '#2B8CE6', '#1F6FD6', '#4FB0F0', '#1F5A78'];
  const tables: Table[] = tpos.map((t, i) => ({ x: t.x, y: t.y, w: TW, h: TH, hue: thues[i], rows: 3 }));
  const rels: Rel[] = [
    { a: 0, b: 3, m: 1 },
    { a: 1, b: 3, m: 0 },
    { a: 1, b: 4, m: 1 },
    { a: 2, b: 4, m: 0 },
    { a: 3, b: 5, m: 1 },
    { a: 3, b: 6, m: 1 },
    { a: 4, b: 6, m: 0 },
    { a: 4, b: 7, m: 1 },
    { a: 6, b: 7, m: 0 },
    { a: 3, b: 4, m: 1 },
  ];
  return { W: 520, H: 372, tables, rels };
}

// ── 메타데이터: 표준용어 방사형 ──────────────────────────────────────────────
function buildMeta() {
  const W = 520;
  const H = 372;
  const cx = W / 2;
  const cy = H * 0.55;
  const center = { x: cx, y: cy, label: '표준용어' };
  const terms = ['데이터', '키워드', '기관명', '구성항목', '분류체계', '산출물', '연관어', '코드'];
  const nodes = terms.map((t, i) => {
    const ang = -Math.PI / 2 + i * ((2 * Math.PI) / terms.length);
    return { x: cx + Math.cos(ang) * 150, y: cy + Math.sin(ang) * 102, label: t };
  });
  return { W, H, center, nodes };
}

// ── SVG 렌더러 ───────────────────────────────────────────────────────────────
const MAP_EDGE_COLOR: Record<number, string> = {
  1: 'rgba(79,176,240,0.9)', // 유사도(시안)
  2: 'rgba(120,135,155,0.45)', // 연관(그레이)
  3: 'rgba(120,135,155,0.4)',
};

function MapSvg({ g, on }: { g: ReturnType<typeof buildMap>; on: boolean }) {
  const delayOf = (n: MapNode, i: number) => (n.level === 0 ? 1.5 : n.level === 1 ? 1.75 + i * 0.04 : 2.05 + i * 0.012);
  const hidden: React.CSSProperties = { opacity: 0 };
  return (
    <svg viewBox={`0 0 ${g.W} ${g.H}`} width="100%" height="100%" style={{ display: 'block' }} aria-hidden>
      {/* 레벨 점선 링 */}
      {g.guides.map((r, i) => (
        <circle
          key={`g${i}`}
          cx={g.mcx}
          cy={g.mcy}
          r={r}
          fill="none"
          stroke="rgba(120,135,155,0.32)"
          strokeWidth={1}
          strokeDasharray="2 6"
          style={
            on
              ? {
                  transformBox: 'fill-box',
                  transformOrigin: 'center',
                  animation: 'dm-pop .6s ease forwards',
                  animationDelay: `${1.45 + i * 0.18}s`,
                  opacity: 0,
                }
              : hidden
          }
        />
      ))}
      {/* faint halo */}
      {g.nodes.map((n, i) => (
        <circle
          key={`h${i}`}
          cx={n.x}
          cy={n.y}
          r={n.rad * 1.5}
          fill={n.c}
          style={
            on ? { opacity: 0, animation: 'dm-halo .5s ease forwards', animationDelay: `${delayOf(n, i)}s` } : hidden
          }
        />
      ))}
      {/* edges */}
      {g.edges.map((e, i) => (
        <line
          key={`e${i}`}
          x1={g.nodes[e.a].x}
          y1={g.nodes[e.a].y}
          x2={g.nodes[e.b].x}
          y2={g.nodes[e.b].y}
          stroke={MAP_EDGE_COLOR[e.lvl] || 'rgba(120,135,155,0.4)'}
          strokeWidth={e.lvl === 1 ? 1.8 : 1.2}
          pathLength={1}
          style={
            on
              ? {
                  strokeDasharray: 1,
                  strokeDashoffset: 1,
                  animation: 'dm-draw .65s ease forwards',
                  animationDelay: `${1.55 + (e.lvl - 1) * 0.25 + i * 0.012}s`,
                }
              : { strokeDasharray: 1, strokeDashoffset: 1 }
          }
        />
      ))}
      {/* nodes */}
      {g.nodes.map((n, i) => (
        <circle
          key={`n${i}`}
          cx={n.x}
          cy={n.y}
          r={n.rad}
          fill={n.c}
          style={
            on
              ? {
                  opacity: 0,
                  transformBox: 'fill-box',
                  transformOrigin: 'center',
                  animation: 'dm-pop .42s ease forwards',
                  animationDelay: `${delayOf(n, i)}s`,
                }
              : hidden
          }
        />
      ))}
    </svg>
  );
}

function SetSvg({ g, on }: { g: ReturnType<typeof buildSet>; on: boolean }) {
  const anchor = (t: Table, tx: number, ty: number) => {
    const cx = t.x + t.w / 2;
    const cy = t.y + t.h / 2;
    const dx = tx - cx;
    const dy = ty - cy;
    const sx = dx ? t.w / 2 / Math.abs(dx) : Infinity;
    const sy = dy ? t.h / 2 / Math.abs(dy) : Infinity;
    const sc = Math.min(sx, sy);
    return { x: cx + dx * sc, y: cy + dy * sc };
  };
  const hidden: React.CSSProperties = { opacity: 0 };
  return (
    <svg viewBox={`0 0 ${g.W} ${g.H}`} width="100%" height="100%" style={{ display: 'block' }} aria-hidden>
      {/* 관계선 + 커넥터 마커 */}
      {g.rels.map((r, i) => {
        const A = g.tables[r.a];
        const B = g.tables[r.b];
        const ca = { x: A.x + A.w / 2, y: A.y + A.h / 2 };
        const cb = { x: B.x + B.w / 2, y: B.y + B.h / 2 };
        const pa = anchor(A, cb.x, cb.y);
        const pb = anchor(B, ca.x, ca.y);
        const one = r.m ? pa : pb;
        const many = r.m ? pb : pa;
        return (
          <g key={`rel${i}`}>
            <line
              x1={pa.x}
              y1={pa.y}
              x2={pb.x}
              y2={pb.y}
              stroke="rgba(120,135,155,0.5)"
              strokeWidth={1.3}
              pathLength={1}
              style={
                on
                  ? {
                      strokeDasharray: 1,
                      strokeDashoffset: 1,
                      animation: 'dm-draw .55s ease forwards',
                      animationDelay: `${1.2 + i * 0.05}s`,
                    }
                  : { strokeDasharray: 1, strokeDashoffset: 1 }
              }
            />
            <circle
              cx={one.x}
              cy={one.y}
              r={2.4}
              fill="#2B8CE6"
              style={
                on
                  ? { opacity: 0, animation: 'dm-pop .3s ease forwards', animationDelay: `${1.55 + i * 0.05}s` }
                  : hidden
              }
            />
            <circle
              cx={many.x}
              cy={many.y}
              r={3.6}
              fill="#ffffff"
              stroke="#2B8CE6"
              strokeWidth={1.3}
              style={
                on
                  ? { opacity: 0, animation: 'dm-pop .3s ease forwards', animationDelay: `${1.55 + i * 0.05}s` }
                  : hidden
              }
            />
          </g>
        );
      })}
      {/* ERD 테이블 */}
      {g.tables.map((t, i) => (
        <g
          key={`tb${i}`}
          style={
            on
              ? {
                  opacity: 0,
                  transformBox: 'fill-box',
                  transformOrigin: 'center',
                  animation: 'dm-pop .4s ease forwards',
                  animationDelay: `${0.95 + i * 0.07}s`,
                }
              : { opacity: 0 }
          }
        >
          <rect x={t.x} y={t.y} width={t.w} height={t.h} rx={5} fill="#ffffff" stroke={t.hue} strokeWidth={1.3} />
          <rect x={t.x} y={t.y} width={t.w} height={15} rx={5} fill={t.hue} />
          <rect x={t.x} y={t.y + 10} width={t.w} height={5} fill={t.hue} />
          {Array.from({ length: t.rows }).map((_, k) => (
            <line
              key={k}
              x1={t.x + 8}
              y1={t.y + 24 + k * 9}
              x2={t.x + t.w - 8}
              y2={t.y + 24 + k * 9}
              stroke="#dfe4ee"
              strokeWidth={1.4}
            />
          ))}
        </g>
      ))}
    </svg>
  );
}

function MetaSvg({ g, on }: { g: ReturnType<typeof buildMeta>; on: boolean }) {
  const pill = (n: { x: number; y: number; label: string }, i: number, isC: boolean) => {
    const w = isC ? 78 : 58;
    const hh = isC ? 34 : 27;
    return (
      <g
        key={`mp${i}`}
        style={
          on
            ? {
                opacity: 0,
                transformBox: 'fill-box',
                transformOrigin: 'center',
                animation: 'dm-pop .45s ease forwards',
                animationDelay: `${isC ? 0.55 : 0.85 + i * 0.05}s`,
              }
            : { opacity: 0 }
        }
      >
        <rect
          x={n.x - w / 2}
          y={n.y - hh / 2}
          width={w}
          height={hh}
          rx={hh / 2}
          fill={isC ? '#2B8CE6' : '#ffffff'}
          stroke={isC ? '#2B8CE6' : '#4FB0F0'}
          strokeWidth={1.3}
        />
        <text
          x={n.x}
          y={n.y + 1}
          fill={isC ? '#ffffff' : '#1F5A78'}
          fontSize={isC ? 15 : 12.5}
          fontWeight={isC ? 800 : 700}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {n.label}
        </text>
      </g>
    );
  };
  return (
    <svg viewBox={`0 0 ${g.W} ${g.H}`} width="100%" height="100%" style={{ display: 'block' }} aria-hidden>
      {g.nodes.map((n, i) => (
        <line
          key={`ml${i}`}
          x1={g.center.x}
          y1={g.center.y}
          x2={n.x}
          y2={n.y}
          stroke="rgba(120,135,155,0.45)"
          strokeWidth={1.4}
          pathLength={1}
          style={
            on
              ? {
                  strokeDasharray: 1,
                  strokeDashoffset: 1,
                  animation: 'dm-draw .6s ease forwards',
                  animationDelay: `${0.6 + i * 0.05}s`,
                }
              : { strokeDasharray: 1, strokeDashoffset: 1 }
          }
        />
      ))}
      {pill(g.center, -1, true)}
      {g.nodes.map((n, i) => pill(n, i, false))}
    </svg>
  );
}

// ── 콜아웃/라벨 데이터 ───────────────────────────────────────────────────────
const LABELS: { layer: Layer; no: string; title: React.ReactNode; top: number; delay: number }[] = [
  { layer: 'map', no: '01', title: '데이터맵', top: 230, delay: 1.5 },
  { layer: 'set', no: '02', title: '데이터셋', top: 442, delay: 1.0 },
  {
    layer: 'meta',
    no: '03',
    title: (
      <>
        메타데이터
        <br />
        어휘사전
      </>
    ),
    top: 604,
    delay: 0.55,
  },
];

const CALLOUTS: { eyebrow: string; body: React.ReactNode; top: number; delay: number }[] = [
  {
    eyebrow: 'USER UI / UX',
    body: (
      <>
        사용자 UI/UX 편의성, <span style={{ color: '#b8313d' }}>조망성 극대화</span>
      </>
    ),
    top: 222,
    delay: 2.5,
  },
  {
    eyebrow: 'RELATION DB',
    body: (
      <>
        데이터셋간 <span style={{ color: '#b8313d' }}>연관도 정보</span> DB 구성
      </>
    ),
    top: 435,
    delay: 1.9,
  },
  {
    eyebrow: 'META VOCABULARY',
    body: (
      <>
        메타데이터 표준용어간의 <span style={{ color: '#b8313d' }}>연관망 DB</span> 구성
      </>
    ),
    top: 602,
    delay: 1.35,
  },
];

// 좁은 화면 폴백용 스텝 카드(기존 .cp-steps 톤)
const STEPS = [
  { no: 'STEP 1', title: '메타데이터 어휘사전', desc: '메타데이터 표준용어 간의 연관망 DB를 구성합니다.' },
  { no: 'STEP 2', title: '데이터셋', desc: '데이터셋 간 연관도 정보 DB를 구성합니다.' },
  { no: 'STEP 3', title: '데이터맵', desc: '사용자 UI·UX 편의성과 조망성을 극대화합니다.' },
];

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
const HowItWorksDiagram: React.FC = () => {
  const map = useMemo(buildMap, []);
  const set = useMemo(buildSet, []);
  const meta = useMemo(buildMeta, []);

  const { ref: viewRef, isIntersecting } = useIntersectionObserver({ threshold: 0.2 });
  const [started, setStarted] = useState(false);
  const [reveal, setReveal] = useState({ map: false, set: false, meta: false });
  const [hovered, setHovered] = useState<Layer | null>(null);
  const [runId, setRunId] = useState(0);
  const [vw, setVw] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    if (isIntersecting) setStarted(true);
  }, [isIntersecting]);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 스크롤 중에는 3D 평면의 hover 상호작용을 차단한다.
  // (커서가 고정된 채 평면이 밑을 지나가면 mouseenter/leave 가 연쇄 발동해
  //  매번 1초짜리 transform·opacity 트랜지션이 일어나 스크롤이 "걸리는" 현상 방지)
  const [scrolling, setScrolling] = useState(false);
  const scrollTimer = useRef<number>();
  useEffect(() => {
    const onScroll = () => {
      setScrolling(true);
      setHovered(null);
      window.clearTimeout(scrollTimer.current);
      scrollTimer.current = window.setTimeout(() => setScrolling(false), 150);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(scrollTimer.current);
    };
  }, []);

  // 좁은 화면(<760px)에서는 3D 대신 단순 스텝 카드로 폴백
  const compact = vw < 760;

  const timers = useRef<number[]>([]);
  useEffect(() => {
    if (!started) return undefined;
    timers.current.forEach(clearTimeout);
    timers.current = [
      window.setTimeout(() => setReveal((s) => ({ ...s, meta: true })), 350),
      window.setTimeout(() => setReveal((s) => ({ ...s, set: true })), 850),
      window.setTimeout(() => setReveal((s) => ({ ...s, map: true })), 1350),
    ];
    return () => timers.current.forEach(clearTimeout);
  }, [started, runId]);

  const replay = () => {
    setReveal({ map: false, set: false, meta: false });
    setHovered(null);
    setRunId((n) => n + 1);
  };

  // 컨테이너 폭에 맞춰 1200px 스테이지를 스케일
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.76);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      setScale(Math.min(0.84, el.clientWidth / STAGE_W));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const planeStyle = (name: Layer, targetZ: number, baseScale: number): React.CSSProperties => {
    const r = reveal[name];
    const z = r ? targetZ : targetZ - 210;
    let s = r ? baseScale : baseScale * 0.78;
    let op = 1;
    if (hovered) {
      if (hovered === name) s = baseScale * 1.06;
      else op = 0.32;
    }
    return {
      position: 'absolute',
      left: 0,
      top: 0,
      width: 600,
      height: 660,
      transformStyle: 'preserve-3d',
      transform: `translateZ(${z}px) scale(${s})`,
      opacity: r ? op : 0,
      transition: 'transform 1s cubic-bezier(.2,.8,.2,1), opacity .8s ease',
      cursor: 'pointer',
    };
  };

  const appear = (delay: number): React.CSSProperties => ({
    opacity: started ? 1 : 0,
    transform: started ? 'none' : 'translateY(18px)',
    transition: `opacity .7s ease ${delay}s, transform .7s ease ${delay}s`,
  });

  const cardChrome: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: 16,
    background: '#ffffff',
    border: '1px solid #e8eaf1',
    boxShadow: '0 30px 56px rgba(24,27,36,0.16), inset 0 1px 0 rgba(255,255,255,0.7)',
    overflow: 'hidden',
  };

  const planes: { name: Layer; z: number; base: number; svg: React.ReactNode; enter: () => void }[] = [
    {
      name: 'map',
      z: 430,
      base: 1.3,
      svg: <MapSvg g={map} on={started} key={`map${runId}`} />,
      enter: () => setHovered('map'),
    },
    {
      name: 'set',
      z: 205,
      base: 1.02,
      svg: <SetSvg g={set} on={started} key={`set${runId}`} />,
      enter: () => setHovered('set'),
    },
    {
      name: 'meta',
      z: 0,
      base: 0.78,
      svg: <MetaSvg g={meta} on={started} key={`meta${runId}`} />,
      enter: () => setHovered('meta'),
    },
  ];

  return (
    <div className="cp-dm3d" ref={viewRef}>
      <div className="cp-head">
        <div className="cp-head__eyebrow">HOW IT WORKS</div>
        <h2 className="cp-head__title">데이터맵의 구성요소 및 단계</h2>
      </div>

      {compact ? (
        <div className="cp-steps" style={{ marginTop: 28 }}>
          {STEPS.map((s) => (
            <div key={s.no} className="cp-steps__card">
              <div className="cp-steps__no">{s.no}</div>
              <div className="cp-steps__title">{s.title}</div>
              <div className="cp-steps__desc">{s.desc}</div>
            </div>
          ))}
        </div>
      ) : (
        /* 스케일 래퍼: 폭에 맞춰 stage 축소, 높이는 스케일에 따라 예약 */
        <div className="cp-dm3d__wrap" ref={wrapRef} style={{ height: STAGE_H * scale }}>
          <div
            className="cp-dm3d__stage"
            style={{ width: STAGE_W, height: STAGE_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}
          >
            {/* 3D 씬 (스크롤 중에는 hover 차단으로 스크롤 끊김 방지) */}
            <div
              style={{
                position: 'absolute',
                left: 300,
                top: 290,
                width: 600,
                height: 660,
                perspective: 1750,
                zIndex: 2,
                pointerEvents: scrolling ? 'none' : undefined,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  transformStyle: 'preserve-3d',
                  transform: 'rotateX(53deg) rotateZ(-31deg)',
                }}
              >
                {planes.map((p) => (
                  <div
                    key={p.name}
                    style={planeStyle(p.name, p.z, p.base)}
                    onMouseEnter={p.enter}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: 40,
                        top: 140,
                        width: 520,
                        height: 372,
                        transformStyle: 'preserve-3d',
                      }}
                    >
                      <div style={cardChrome}>{p.svg}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 좌측 레이어 라벨 */}
            {LABELS.map((l) => (
              <div key={l.no} style={{ position: 'absolute', left: 24, top: l.top, zIndex: 4, ...appear(l.delay) }}>
                <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: '#b8313d' }}>{l.no}</div>
                <div style={{ fontSize: 23, fontWeight: 800, color: '#181b24', marginTop: 2 }}>{l.title}</div>
              </div>
            ))}

            {/* 우측 콜아웃 */}
            {CALLOUTS.map((c) => (
              <div
                key={c.eyebrow}
                style={{ position: 'absolute', left: 898, top: c.top, width: 248, zIndex: 4, ...appear(c.delay) }}
              >
                <div
                  style={{
                    padding: '16px 18px',
                    borderRadius: 14,
                    background: '#ffffff',
                    border: '1px solid #e8eaf1',
                    boxShadow: '0 16px 32px rgba(24,27,36,0.10)',
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#8494ae', marginBottom: 6 }}>
                    {c.eyebrow}
                  </div>
                  <div
                    style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.5, color: '#181b24', wordBreak: 'keep-all' }}
                  >
                    {c.body}
                  </div>
                </div>
              </div>
            ))}

            {/* 하단 흐름 캡션 */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 4,
                ...appear(3),
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '11px 22px',
                  borderRadius: 999,
                  background: '#ffffff',
                  border: '1px solid #e8eaf1',
                  boxShadow: '0 10px 24px rgba(24,27,36,0.06)',
                  fontSize: 13.5,
                  color: '#565b70',
                }}
              >
                <span style={{ color: '#1F5A78', fontWeight: 700 }}>메타데이터 어휘사전</span>
                <span style={{ color: '#b9c2d2' }}>→</span>
                <span style={{ color: '#1F6FD6', fontWeight: 700 }}>데이터셋</span>
                <span style={{ color: '#b9c2d2' }}>→</span>
                <span style={{ color: '#2B8CE6', fontWeight: 700 }}>데이터맵</span>
                <span style={{ marginLeft: 6, color: '#8494ae' }}>데이터는 아래에서 위로 정제·시각화됩니다</span>
              </div>
            </div>

            {/* 다시 재생 */}
            <button
              type="button"
              onClick={replay}
              style={{
                position: 'absolute',
                right: 6,
                bottom: 6,
                zIndex: 7,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '8px 14px',
                borderRadius: 999,
                cursor: 'pointer',
                background: '#ffffff',
                border: '1px solid #e8eaf1',
                color: '#565b70',
                fontFamily: 'inherit',
                fontSize: 12.5,
                fontWeight: 600,
                boxShadow: '0 6px 16px rgba(24,27,36,0.06)',
              }}
            >
              ↻ 다시 재생
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HowItWorksDiagram;
